import { jsPDF } from 'jspdf';
import {
  TimelineEvent,
  TimelineExportFormat,
  TimelineExportOptions,
  TimelineStatistics,
} from '../types/timeline';
import { LifeEventType } from '../types/person';

/**
 * Timeline Export Service
 * Handles exporting timeline data to various formats (PDF, CSV, GEDCOM)
 *
 * Part of Phase 3.3: Interactive Timeline Page
 */
export class TimelineExportService {
  /**
   * Export timeline events to the specified format
   *
   * @param events - Array of timeline events to export
   * @param statistics - Optional timeline statistics to include
   * @param options - Export options
   * @returns Blob containing the exported data
   */
  static async exportTimeline(
    events: TimelineEvent[],
    statistics: TimelineStatistics | null,
    options: TimelineExportOptions
  ): Promise<Blob> {
    switch (options.format) {
      case 'pdf':
        return this.exportToPDF(events, statistics, options);
      case 'csv':
        return this.exportToCSV(events, options);
      case 'gedcom':
        return this.exportToGEDCOM(events, options);
      default:
        throw new Error(`Unsupported export format: ${options.format}`);
    }
  }

  /**
   * Export timeline to PDF format
   */
  static async exportToPDF(
    events: TimelineEvent[],
    statistics: TimelineStatistics | null,
    options: TimelineExportOptions
  ): Promise<Blob> {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let y = margin;

      // Helper to check if we need a new page
      const checkNewPage = (lineHeight: number = 10) => {
        if (y + lineHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
          return true;
        }
        return false;
      };

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Family Timeline', margin, y);
      y += 15;

      // Date Generated
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, y);
      y += 10;

      // Statistics Section (if enabled)
      if (options.includeStatistics && statistics) {
        checkNewPage(40);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Timeline Statistics', margin, y);
        y += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Total Events: ${statistics.totalEvents}`, margin + 5, y);
        y += 6;
        doc.text(`People Included: ${statistics.peopleIncluded}`, margin + 5, y);
        y += 6;

        if (statistics.dateRange.earliest && statistics.dateRange.latest) {
          doc.text(
            `Date Range: ${statistics.dateRange.earliest} to ${statistics.dateRange.latest}`,
            margin + 5,
            y
          );
          y += 6;
          doc.text(`Time Span: ${statistics.dateRange.spanYears} years`, margin + 5, y);
          y += 6;
        }

        y += 10;
      }

      // Events Section
      checkNewPage();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Timeline Events', margin, y);
      y += 10;

      // Event list
      doc.setFontSize(9);
      events.forEach((event, index) => {
        checkNewPage(25);

        // Event header
        doc.setFont('helvetica', 'bold');
        const header = `${event.personName} - ${this.formatEventType(event.type)}`;
        doc.text(header, margin, y);
        y += 5;

        // Event date
        doc.setFont('helvetica', 'normal');
        if (event.date) {
          doc.text(`Date: ${this.formatDate(event.date)}`, margin + 5, y);
          y += 5;
        }

        // Event location
        if (event.location) {
          const locationText = this.formatLocation(event.location);
          if (locationText) {
            doc.text(`Location: ${locationText}`, margin + 5, y);
            y += 5;
          }
        }

        // Event description
        if (event.description) {
          doc.text(`Description: ${event.description.substring(0, 100)}`, margin + 5, y);
          y += 5;
        }

        // Generation info
        if (event.generation !== undefined) {
          doc.text(`Generation: ${event.generation + 1}`, margin + 5, y);
          y += 5;
        }

        y += 3; // Space between events
      });

      // Return as Blob
      return doc.output('blob');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      throw new Error('Failed to generate PDF export');
    }
  }

  /**
   * Export timeline to CSV format
   */
  static async exportToCSV(
    events: TimelineEvent[],
    options: TimelineExportOptions
  ): Promise<Blob> {
    try {
      // CSV headers
      const headers = [
        'Person Name',
        'Gender',
        'Event Type',
        'Date',
        'Location',
        'Description',
        'Generation',
        'Decade',
        'Century',
        'Sources',
      ];

      // Build CSV rows
      const rows: string[][] = [headers];

      events.forEach(event => {
        const row = [
          event.personName,
          event.personGender,
          this.formatEventType(event.type),
          event.date || '',
          this.formatLocation(event.location),
          event.description.replace(/"/g, '""'), // Escape quotes
          event.generation !== undefined ? String(event.generation + 1) : '',
          event.decade || '',
          event.century || '',
          event.sources?.join('; ') || '',
        ];

        rows.push(row);
      });

      // Convert to CSV string
      const csvContent = rows
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Return as Blob
      return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    } catch (error) {
      console.error('Failed to export CSV:', error);
      throw new Error('Failed to generate CSV export');
    }
  }

  /**
   * Export timeline to GEDCOM format (basic implementation)
   */
  static async exportToGEDCOM(
    events: TimelineEvent[],
    options: TimelineExportOptions
  ): Promise<Blob> {
    try {
      // GEDCOM header
      let gedcom = '0 HEAD\n';
      gedcom += '1 SOUR Martz Genealogy\n';
      gedcom += '2 NAME Martz Family Timeline\n';
      gedcom += '1 GEDC\n';
      gedcom += '2 VERS 5.5.1\n';
      gedcom += '2 FORM LINEAGE-LINKED\n';
      gedcom += '1 CHAR UTF-8\n';
      gedcom += `1 DATE ${this.formatGedcomDate(new Date())}\n`;

      // Group events by person
      const eventsByPerson = new Map<string, TimelineEvent[]>();
      events.forEach(event => {
        const personEvents = eventsByPerson.get(event.personId) || [];
        personEvents.push(event);
        eventsByPerson.set(event.personId, personEvents);
      });

      // Create INDI records for each person with their events
      let indiId = 1;
      eventsByPerson.forEach((personEvents, personId) => {
        const firstEvent = personEvents[0];

        gedcom += `0 @I${indiId}@ INDI\n`;
        gedcom += `1 NAME ${firstEvent.personName}\n`;
        gedcom += `1 SEX ${this.getGedcomGender(firstEvent.personGender)}\n`;

        // Add events
        personEvents.forEach(event => {
          switch (event.type) {
            case 'birth':
              gedcom += '1 BIRT\n';
              if (event.date) gedcom += `2 DATE ${this.formatGedcomDate(event.date)}\n`;
              if (event.location) gedcom += `2 PLAC ${this.formatLocation(event.location)}\n`;
              break;

            case 'death':
              gedcom += '1 DEAT\n';
              if (event.date) gedcom += `2 DATE ${this.formatGedcomDate(event.date)}\n`;
              if (event.location) gedcom += `2 PLAC ${this.formatLocation(event.location)}\n`;
              break;

            case 'marriage':
              gedcom += '1 MARR\n';
              if (event.date) gedcom += `2 DATE ${this.formatGedcomDate(event.date)}\n`;
              if (event.location) gedcom += `2 PLAC ${this.formatLocation(event.location)}\n`;
              break;

            default:
              // Generic event
              gedcom += '1 EVEN\n';
              gedcom += `2 TYPE ${this.formatEventType(event.type)}\n`;
              if (event.date) gedcom += `2 DATE ${this.formatGedcomDate(event.date)}\n`;
              if (event.location) gedcom += `2 PLAC ${this.formatLocation(event.location)}\n`;
              if (event.description) gedcom += `2 NOTE ${event.description}\n`;
              break;
          }
        });

        indiId++;
      });

      // GEDCOM trailer
      gedcom += '0 TRLR\n';

      // Return as Blob
      return new Blob([gedcom], { type: 'text/plain;charset=utf-8;' });
    } catch (error) {
      console.error('Failed to export GEDCOM:', error);
      throw new Error('Failed to generate GEDCOM export');
    }
  }

  /**
   * Generate shareable link with encoded filter state
   */
  static generateShareableLink(
    filter: any,
    viewMode: string,
    baseUrl: string = window.location.origin
  ): string {
    const state = {
      filter,
      viewMode,
      timestamp: new Date().toISOString(),
    };

    // Encode state as base64
    const encoded = btoa(JSON.stringify(state));

    // Generate URL with query param
    return `${baseUrl}/timeline?share=${encoded}`;
  }

  /**
   * Decode shareable link state
   */
  static decodeShareableLink(encodedState: string): any {
    try {
      const decoded = atob(encodedState);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to decode shareable link:', error);
      return null;
    }
  }

  // Helper methods

  private static formatEventType(type: LifeEventType): string {
    const typeMap: Record<LifeEventType, string> = {
      birth: 'Birth',
      death: 'Death',
      marriage: 'Marriage',
      divorce: 'Divorce',
      education: 'Education',
      employment: 'Employment',
      military: 'Military Service',
      immigration: 'Immigration',
      residence: 'Residence',
      achievement: 'Achievement',
      other: 'Other Event',
    };

    return typeMap[type] || type;
  }

  private static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  private static formatLocation(location?: {
    city?: string;
    state?: string;
    country?: string;
  }): string {
    if (!location) return '';

    const parts = [location.city, location.state, location.country].filter(Boolean);
    return parts.join(', ');
  }

  private static formatGedcomDate(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;

    const months = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];

    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();

    return `${day} ${month} ${year}`;
  }

  private static getGedcomGender(gender: string): string {
    if (gender === 'male') return 'M';
    if (gender === 'female') return 'F';
    return 'U'; // Unknown
  }

  /**
   * Download exported file
   */
  static downloadFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export default TimelineExportService;
